import { useState, useEffect } from 'react';
import {
  Plus,
  Trash2,
  Edit2,
  Server,
  RefreshCw,
  Shield,
  Clock,
  AlertCircle,
  X,
  Globe,
  Key,
} from 'lucide-react';
import {
  imageBackends,
  ImageBackend,
  CreateImageBackendRequest,
  UpdateImageBackendRequest,
} from '../../api/client';
import { formatDateTime, formatNumber, cn } from '../../lib/utils';

export default function ImageBackends() {
  const [backends, setBackends] = useState<ImageBackend[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingBackend, setEditingBackend] = useState<ImageBackend | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      const res = await imageBackends.list();
      setBackends(res.backends);
      setError(null);
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to fetch backends';
      console.error('Failed to fetch backends:', error);
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 15000); // Refresh every 15s
    return () => clearInterval(interval);
  }, []);

  const handleCreateBackend = async (data: CreateImageBackendRequest) => {
    try {
      setError(null);
      await imageBackends.create(data);
      setShowCreateModal(false);
      await fetchData();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to create backend';
      console.error('Failed to create backend:', error);
      setError(message);
    }
  };

  const handleUpdateBackend = async (id: string, data: UpdateImageBackendRequest) => {
    try {
      setError(null);
      await imageBackends.update(id, data);
      setEditingBackend(null);
      await fetchData();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'Failed to update backend';
      console.error('Failed to update backend:', error);
      setError(message);
    }
  };

  const handleToggleActive = async (backend: ImageBackend) => {
    try {
      await imageBackends.update(backend.id, { isActive: !backend.isActive });
      await fetchData();
    } catch (error) {
      console.error('Failed to toggle backend:', error);
    }
  };

  const handleDeleteBackend = async (backend: ImageBackend) => {
    if (!confirm(`Are you sure you want to delete backend "${backend.name}"?`)) {
      return;
    }

    try {
      await imageBackends.delete(backend.id);
      await fetchData();
    } catch (error) {
      console.error('Failed to delete backend:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex items-center gap-3 text-muted-foreground">
          <RefreshCw className="w-5 h-5 animate-spin" />
          <span>Loading backends...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between animate-fade-in-up">
        <div>
          <h1 className="text-3xl font-bold text-gradient-violet">Image Backends</h1>
          <p className="text-muted-foreground mt-1">Manage ComfyUI server connections</p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="btn-glow-violet px-4 py-2.5 rounded-xl flex items-center gap-2 text-sm"
        >
          <span className="flex items-center gap-2">
            <Plus className="w-4 h-4" />
            Add Backend
          </span>
        </button>
      </div>

      {/* Error Alert */}
      {error && (
        <div className="bg-rose-500/10 border border-rose-500/30 rounded-xl p-5 animate-scale-in">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-xl bg-rose-500/20 flex items-center justify-center flex-shrink-0">
              <AlertCircle className="w-5 h-5 text-rose-400" />
            </div>
            <div className="flex-1">
              <p className="font-medium text-rose-400 mb-1">Error</p>
              <p className="text-sm text-rose-300/80">{error}</p>
            </div>
            <button
              onClick={() => setError(null)}
              className="p-2 rounded-lg bg-rose-500/10 text-rose-400 hover:bg-rose-500/20 transition-all"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Backends Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {backends.map((backend, index) => (
          <BackendCard
            key={backend.id}
            backend={backend}
            delay={index * 100}
            onEdit={() => setEditingBackend(backend)}
            onToggle={() => handleToggleActive(backend)}
            onDelete={() => handleDeleteBackend(backend)}
          />
        ))}
        {backends.length === 0 && (
          <div className="col-span-2 card-glow rounded-2xl p-16">
            <div className="flex flex-col items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-secondary flex items-center justify-center">
                <Server className="w-8 h-8 text-muted-foreground" />
              </div>
              <div className="text-center">
                <p className="text-muted-foreground mb-1">No backends configured</p>
                <p className="text-sm text-muted-foreground/70">Add a ComfyUI backend to start generating images</p>
              </div>
              <button
                onClick={() => setShowCreateModal(true)}
                className="btn-secondary px-4 py-2 rounded-lg text-sm"
              >
                Add your first backend
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Create Backend Modal */}
      {showCreateModal && (
        <BackendModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={(data) => handleCreateBackend(data as CreateImageBackendRequest)}
        />
      )}

      {/* Edit Backend Modal */}
      {editingBackend && (
        <BackendModal
          existingBackend={editingBackend}
          onClose={() => setEditingBackend(null)}
          onSubmit={(data) => handleUpdateBackend(editingBackend.id, data)}
        />
      )}
    </div>
  );
}

function BackendCard({
  backend,
  delay,
  onEdit,
  onToggle,
  onDelete,
}: {
  backend: ImageBackend;
  delay: number;
  onEdit: () => void;
  onToggle: () => void;
  onDelete: () => void;
}) {
  const statusStyles: Record<string, { bg: string; text: string; badge: string }> = {
    emerald: { bg: 'bg-emerald-500/10', text: 'text-emerald-400', badge: 'bg-emerald-500/15 text-emerald-400' },
    gray: { bg: 'bg-gray-500/10', text: 'text-gray-400', badge: 'bg-gray-500/15 text-gray-400' },
  };
  const statusKey = backend.isActive ? 'emerald' : 'gray';
  const statusText = backend.isActive ? 'Active' : 'Inactive';
  const styles = statusStyles[statusKey];

  const successRate = backend.stats.totalRequests > 0
    ? ((backend.stats.successfulRequests / backend.stats.totalRequests) * 100).toFixed(1)
    : '0';

  return (
    <div
      className="card-glow rounded-2xl p-6 animate-fade-in-up"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* Header */}
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className={cn(
            'w-12 h-12 rounded-xl flex items-center justify-center',
            styles.bg
          )}>
            <Server className={cn('w-6 h-6', styles.text)} />
          </div>
          <div>
            <h3 className="font-semibold text-lg">{backend.name}</h3>
            <div className="flex items-center gap-2 mt-1">
              <span className={cn(
                'px-2 py-0.5 text-xs font-medium rounded-md capitalize',
                styles.badge
              )}>
                {statusText}
              </span>
              <span className="text-xs text-muted-foreground capitalize">{backend.type}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={onEdit}
            className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-violet-400 transition-colors"
            title="Edit"
          >
            <Edit2 className="w-4 h-4" />
          </button>
          <button
            onClick={onDelete}
            className="p-2 rounded-lg bg-secondary text-muted-foreground hover:text-rose-400 transition-colors"
            title="Delete"
          >
            <Trash2 className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* URL */}
      <div className="flex items-center gap-2 mb-4 px-3 py-2 rounded-lg bg-secondary/50">
        <Globe className="w-4 h-4 text-muted-foreground" />
        <code className="text-sm font-mono text-muted-foreground truncate flex-1" title={backend.baseUrl}>
          {backend.baseUrl}
        </code>
        {backend.hasApiKey && (
          <div className="flex items-center gap-1 text-xs text-amber-400">
            <Key className="w-3 h-3" />
            <span>API Key</span>
          </div>
        )}
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-4 gap-3 mb-4">
        <div className="text-center p-2 rounded-lg bg-secondary/30">
          <p className="text-xs text-muted-foreground">Requests</p>
          <p className="font-mono font-medium">{formatNumber(backend.stats.totalRequests)}</p>
        </div>
        <div className="text-center p-2 rounded-lg bg-secondary/30">
          <p className="text-xs text-muted-foreground">Success</p>
          <p className="font-mono font-medium text-emerald-400">{successRate}%</p>
        </div>
        <div className="text-center p-2 rounded-lg bg-secondary/30">
          <p className="text-xs text-muted-foreground">Avg Time</p>
          <p className="font-mono font-medium">{(backend.stats.avgResponseTime / 1000).toFixed(1)}s</p>
        </div>
        <div className="text-center p-2 rounded-lg bg-secondary/30">
          <p className="text-xs text-muted-foreground">Priority</p>
          <p className="font-mono font-medium">{backend.priority}</p>
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between pt-4 border-t border-border/50">
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock className="w-3.5 h-3.5" />
          <span>Created {formatDateTime(backend.createdAt)}</span>
        </div>
        <button
          onClick={onToggle}
          className={cn(
            'px-3 py-1.5 text-xs font-medium rounded-lg transition-all',
            backend.isActive
              ? 'bg-emerald-500/15 text-emerald-400 hover:bg-emerald-500/25'
              : 'bg-secondary text-muted-foreground hover:text-foreground'
          )}
        >
          {backend.isActive ? 'Enabled' : 'Disabled'}
        </button>
      </div>
    </div>
  );
}

function BackendModal({
  existingBackend,
  onClose,
  onSubmit,
}: {
  existingBackend?: ImageBackend;
  onClose: () => void;
  onSubmit: (data: CreateImageBackendRequest | UpdateImageBackendRequest) => void;
}) {
  const isEditing = !!existingBackend;

  const [name, setName] = useState(existingBackend?.name || '');
  const [type, setType] = useState<'runpod' | 'local' | 'atlas' | 'groq' | 'nanogpt' | 'gemini-edit'>(existingBackend?.type || 'runpod');
  const [baseUrl, setBaseUrl] = useState(existingBackend?.baseUrl || '');
  const [apiKey, setApiKey] = useState('');
  const [priority, setPriority] = useState(existingBackend?.priority?.toString() || '100');
  const [timeout, setTimeout] = useState((existingBackend?.timeout || 120000).toString());
  const [maxConcurrent, setMaxConcurrent] = useState((existingBackend?.maxConcurrent || 5).toString());
  // Default model — only used by gemini-edit; field is rendered conditionally
  // below. State is kept regardless of `type` so toggling away and back
  // doesn't lose the user's input.
  const [defaultModel, setDefaultModel] = useState(existingBackend?.defaultModel ?? '');

  const parseOptionalNumber = (value: string): number | undefined => {
    const trimmed = value.trim();
    if (!trimmed) return undefined;

    const parsed = Number(trimmed);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const priorityValue = parseOptionalNumber(priority);
    const timeoutValue = parseOptionalNumber(timeout);
    const maxConcurrentValue = parseOptionalNumber(maxConcurrent);

    if (isEditing) {
      const data: UpdateImageBackendRequest = {};
      if (name !== existingBackend.name) data.name = name;
      if (baseUrl !== existingBackend.baseUrl) data.baseUrl = baseUrl;
      if (apiKey) data.apiKey = apiKey;
      if (priorityValue !== undefined && priorityValue !== existingBackend.priority) data.priority = priorityValue;
      if (timeoutValue !== undefined && timeoutValue !== existingBackend.timeout) data.timeout = timeoutValue;
      if (maxConcurrentValue !== undefined && maxConcurrentValue !== existingBackend.maxConcurrent) data.maxConcurrent = maxConcurrentValue;
      // Only send defaultModel for gemini-edit backends. Other types
      // ignore the field server-side, but skipping the patch keeps
      // the request body minimal. Empty input → null (clears any prior
      // value); non-empty diff → push the new string.
      if (existingBackend.type === 'gemini-edit') {
        const trimmed = defaultModel.trim();
        const previous = existingBackend.defaultModel ?? '';
        if (trimmed !== previous) {
          data.defaultModel = trimmed.length === 0 ? null : trimmed;
        }
      }
      onSubmit(data);
    } else {
      const data: CreateImageBackendRequest = {
        name,
        type,
        baseUrl,
      };
      if (priorityValue !== undefined) data.priority = priorityValue;
      if (timeoutValue !== undefined) data.timeout = timeoutValue;
      if (maxConcurrentValue !== undefined) data.maxConcurrent = maxConcurrentValue;
      if (apiKey) data.apiKey = apiKey;
      // Only attach defaultModel for gemini-edit at create time. For other
      // types the field is hidden in the form, so its state would just be ''.
      if (type === 'gemini-edit') {
        const trimmed = defaultModel.trim();
        if (trimmed.length > 0) data.defaultModel = trimmed;
      }
      onSubmit(data);
    }
  };

  return (
    <div className="fixed inset-0 modal-overlay flex items-center justify-center z-50 p-4">
      <div
        className="modal-content rounded-2xl max-w-lg w-full p-6 max-h-[90vh] overflow-y-auto animate-scale-in"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-xl bg-violet-500/10 flex items-center justify-center">
            {isEditing ? (
              <Edit2 className="w-5 h-5 text-violet-400" />
            ) : (
              <Server className="w-5 h-5 text-violet-400" />
            )}
          </div>
          <div>
            <h2 className="text-lg font-semibold">
              {isEditing ? 'Edit Backend' : 'Add Backend'}
            </h2>
            <p className="text-sm text-muted-foreground">
              {isEditing ? 'Update backend configuration' : 'Configure a new ComfyUI server'}
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Name
            </label>
            <input
              type="text"
              required
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="input-neural w-full px-4 py-3 rounded-xl text-sm"
              placeholder="RunPod GPU"
            />
          </div>

          {/* Type (only for new backends) */}
          {!isEditing && (
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Type
              </label>
              <select
                value={type}
                onChange={(e) => setType(e.target.value as 'runpod' | 'local' | 'atlas' | 'groq' | 'nanogpt' | 'gemini-edit')}
                className="input-neural w-full px-4 py-3 rounded-xl text-sm cursor-pointer"
              >
                <option value="runpod">RunPod Serverless</option>
                <option value="local">Local ComfyUI</option>
                <option value="atlas">Atlas Cloud</option>
                <option value="groq">Groq Imagine</option>
                <option value="nanogpt">NanoGPT</option>
                <option value="gemini-edit">Gemini Image Edit</option>
              </select>
            </div>
          )}

          {/* Base URL */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              Base URL
            </label>
            <input
              type="url"
              required
              value={baseUrl}
              onChange={(e) => setBaseUrl(e.target.value)}
              className="input-neural w-full px-4 py-3 rounded-xl text-sm font-mono"
              placeholder="https://api.runpod.ai/v2/your-endpoint"
            />
          </div>

          {/* API Key */}
          <div>
            <label className="block text-sm font-medium text-muted-foreground mb-2">
              API Key {isEditing && existingBackend?.hasApiKey && (
                <span className="text-amber-400">(leave blank to keep current)</span>
              )}
            </label>
            <input
              type="password"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              className="input-neural w-full px-4 py-3 rounded-xl text-sm font-mono"
              placeholder={isEditing && existingBackend?.hasApiKey ? '••••••••' : 'Optional API key'}
            />
          </div>

          {/* Default Model (gemini-edit only). Other backend types resolve
              their model from per-workflow provider_params, so the field
              is hidden to avoid confusion. Toggling type away and back
              preserves the entered value via the persistent state above. */}
          {(isEditing ? existingBackend?.type === 'gemini-edit' : type === 'gemini-edit') && (
            <div>
              <label className="block text-sm font-medium text-muted-foreground mb-2">
                Default Model
                <span className="text-xs text-muted-foreground ml-2 font-normal">
                  (optional — leave blank to use built-in default)
                </span>
              </label>
              <input
                type="text"
                value={defaultModel}
                onChange={(e) => setDefaultModel(e.target.value)}
                className="input-neural w-full px-4 py-3 rounded-xl text-sm font-mono"
                placeholder="gemini-3.1-flash-image-preview"
              />
              <p className="text-xs text-muted-foreground mt-2">
                Per-call <code className="text-xs">model</code> in the request body still wins. When that's absent, this value is used. When this is also blank, the worker's built-in default applies.
              </p>
            </div>
          )}

          {/* Advanced Settings */}
          <div className="card-glow rounded-xl p-5 space-y-4">
            <div className="flex items-center gap-2 mb-2">
              <Shield className="w-4 h-4 text-amber-400" />
              <span className="text-sm font-medium">Advanced Settings</span>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="block text-xs text-muted-foreground mb-2">Priority</label>
                <input
                  type="number"
                  value={priority}
                  onChange={(e) => setPriority(e.target.value)}
                  className="input-neural w-full px-3 py-2.5 rounded-lg text-sm font-mono"
                  placeholder="100"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-2">Timeout (ms)</label>
                <input
                  type="number"
                  value={timeout}
                  onChange={(e) => setTimeout(e.target.value)}
                  className="input-neural w-full px-3 py-2.5 rounded-lg text-sm font-mono"
                  placeholder="120000"
                />
              </div>
              <div>
                <label className="block text-xs text-muted-foreground mb-2">Max Concurrent</label>
                <input
                  type="number"
                  value={maxConcurrent}
                  onChange={(e) => setMaxConcurrent(e.target.value)}
                  className="input-neural w-full px-3 py-2.5 rounded-lg text-sm font-mono"
                  placeholder="5"
                />
              </div>
            </div>
            <p className="text-xs text-muted-foreground">
              Lower priority = higher preference. Timeout in milliseconds.
            </p>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-border">
            <button
              type="button"
              onClick={onClose}
              className="btn-secondary px-5 py-2.5 rounded-xl text-sm"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="btn-glow-violet px-5 py-2.5 rounded-xl text-sm"
            >
              <span>{isEditing ? 'Save Changes' : 'Add Backend'}</span>
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
